import React, { useMemo, useState } from "react";

import { Stack, Typography } from "@mui/material";
import { RichTextEditor } from "components/forms";
import { usePrompt } from "hooks";
import { debounce } from "lodash";
import { usePickUserPermissions, useUserRoleSelector } from "store";
import { DocumentSectionMeetingNotes, UserPermissionType } from "store/types";
import { isRoleAnalyst } from "utils/user";

import { RoleName } from "../../../constants";
import { useContentContext } from "../Content/content-context";
import { MeetingHeader } from "../Section-components/Meeting/MeetingHeader";
import { MeetingViewerContent } from "../Section-components/Meeting/MeetingViewerContent";

interface Props {
  section: DocumentSectionMeetingNotes;
}

export const MeetingSection: React.FC<Props> = ({ section }) => {
  const role = useUserRoleSelector();
  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;
  const isReadOnlyUser =
    role?.name === RoleName.Guest || role?.name === RoleName.Viewer || role?.name === RoleName.TeamViewer;

  const { meeting } = section;
  if (!meeting) return null;
  const [description, setDescription] = useState<string>(meeting.description || "");
  const openPrompt = usePrompt();
  const { updateMeetingSection } = useContentContext();
  const handleUpdateDescription = async (description: string) => {
    await updateMeetingSection({
      meetingId: meeting.id,
      body: {
        description
      }
    });
  };

  const handleChange = debounce((event: any) => {
    const newDescription = event.value;
    setDescription(newDescription);
    handleUpdateDescription(newDescription);
  }, 300);

  const isUpdateAvailable = useMemo(
    () => !isReadOnlyUser && !noAccessPermission && !isViewerPermission && isRoleAnalyst({ role }),
    [noAccessPermission, isViewerPermission, role]
  );

  return (
    <>
      <Stack
        tabIndex={0}
        sx={{
          margin: "1.5px",
          padding: "10px",
          boxShadow: "0px 0px 3px 0px rgba(0, 0, 0, 0.25)",
          flexGrow: 1,
          minHeight: "150px"
        }}
        gap="10px"
      >
        <MeetingHeader
          meeting={section.meeting}
          documentId={section.document_id}
          sectionId={section.id}
          menuAvailable={true}
          isUpdateAvailable={isUpdateAvailable}
        />
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ fontSize: "14px", fontWeight: "700", color: "#071F35" }}>
            {meeting.is_ai ? "Summary" : "Note"}
          </Typography>
          {meeting.is_ai && (
            <Typography
              onClick={async () => {
                await openPrompt({
                  Component: MeetingViewerContent,
                  props: {
                    meeting: section.meeting,
                    documentId: section.document_id,
                    sectionId: section.id,
                    defaultTab: "Transcription"
                  }
                });
              }}
              sx={{ fontSize: "14px", fontWeight: "400", color: "#26A6AE", cursor: "pointer" }}
            >
              See Transcription
            </Typography>
          )}
        </Stack>
        <Stack sx={{ ".e-content": { color: "#071F35 !important" }, maxHeight: "500px", overflow: "auto" }}>
          <RichTextEditor
            readonly={!isUpdateAvailable}
            cssClass="documents__meeting-section"
            id={"meetings-" + section?.id}
            change={handleChange}
            saveInterval={1}
            value={description || ""}
            enabled={!section?.meeting?.is_ai}
            placeholder="Add Note Here"
            inlineMode={{
              enable: true,
              onSelection: true
            }}
          />
        </Stack>
      </Stack>
    </>
  );
};
